import React, { useEffect, useRef, useState, useCallback } from 'react';
import { PayloadAction } from '@reduxjs/toolkit';
import InfiniteScroll from 'react-infinite-scroller';
import { Spin, Row, Col } from 'antd';
import pluralize from 'pluralize';

import {
  CustomerPagedResponseModel,
  CustomerInfoResponseModel,
  CustomerStatusFilterEnum,
} from 'api/backofficeApiClient';
import ClientCard from '../ClientCard';
import { getClientRequests } from 'redux/reducers/clientSlice';
import { clientDraftCreatedAtSelector } from 'redux/selectors/clientSelectors';
import { useAppDispatch } from 'hooks/useAppDispatch';
import { useAppSelector } from 'hooks/useAppSelector';

export default function ClientDraftsSection() {
  const latestRequestId = useRef(0);
  const infiniteScrollParentRef = useRef(null);
  const [clients, setClients] = useState([] as CustomerInfoResponseModel[]);
  const [totalCount, setTotalCount] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [isInitialDataLoaded, setIsInitialDataLoaded] = useState(false);
  const dispatch = useAppDispatch();
  const clientDraftCreatedAt = useAppSelector(clientDraftCreatedAtSelector);

  useEffect(() => {
    initInfinityScroll();
  }, [clientDraftCreatedAt]);

  const initInfinityScroll = () => {
    setIsVisible(false);
    setClients([]);
    setIsInitialDataLoaded(false);
    setHasMore(true);
    setTotalCount(0);
    const timer = setTimeout(() => {
      // to destroy InfiniteScroll
      setIsVisible(true);
    }, 0);

    return () => clearTimeout(timer);
  };

  const getTotalCountLabel = () => {
    const requestWord = pluralize('request', totalCount);

    return `${totalCount} draft ${requestWord}`;
  };

  const getNotFoundMessage = () => {
    return 'You have no draft customers at the moment.';
  };

  const onSetClients = useCallback(
    (result: PayloadAction<CustomerPagedResponseModel>) => {
      const loadedClients = result.payload.details || [];
      const totalCount = result.payload.totalCount || 0;
      const newClients = [...clients, ...loadedClients];
      const hasMoreRows = newClients.length < totalCount;

      setHasMore(hasMoreRows);
      setClients(newClients);
      setTotalCount(totalCount);
    },
    [clients],
  );

  const onLoadMore = useCallback(
    async (pageNumber: number) => {
      const requestId = Date.now();
      latestRequestId.current = requestId;

      const result = await dispatch(
        getClientRequests({
          pageNumber,
          requestId,
          statusFilter: CustomerStatusFilterEnum.Draft,
        }),
      );

      if (result.meta.arg.requestId === latestRequestId.current) {
        if (getClientRequests.fulfilled.match(result)) {
          onSetClients(result);
        } else {
          setHasMore(false);
        }

        setIsInitialDataLoaded(true);
      }
    },
    [dispatch, onSetClients],
  );

  return (
    <div ref={infiniteScrollParentRef} className="page-wrapper scroll-box">
      <div className="page-box page-holder page-inner">
        <div className="page-inner-container">
          <Row gutter={[10, 10]}>
            <Col xs={24}>
              {isInitialDataLoaded && (
                <>
                  {!!totalCount && (
                    <p className="page-total-count">{getTotalCountLabel()}</p>
                  )}

                  {!totalCount && (
                    <span className="no-data-msg">{getNotFoundMessage()}</span>
                  )}
                </>
              )}

              {isVisible && (
                <InfiniteScroll
                  pageStart={-1}
                  loadMore={onLoadMore}
                  hasMore={hasMore}
                  loader={<Spin key={0} />}
                  useWindow={false}
                  getScrollParent={() => infiniteScrollParentRef.current}
                >
                  <Row gutter={[20, 20]}>
                    {clients.map((client) => (
                      <Col
                        key={client.customerId}
                        xs={24}
                        md={12}
                        className="d-flex"
                      >
                        <ClientCard client={client} className="stretch-card" />
                      </Col>
                    ))}
                  </Row>
                </InfiniteScroll>
              )}
            </Col>
          </Row>
        </div>
      </div>
    </div>
  );
}
